// Operator OS — v1 (Sprint Feb 8, 2026)
//
// Compact dashboard widget that turns the creator dashboard into a small-
// business OS. Three cards: 30-day churn, 30-day cohort retention, tax CSV
// export. Sized to fit beneath the silo cards on the dashboard so the
// creator sees their business health alongside their silos.

import { useEffect, useState } from "react";
import { ChartLine, UserMinus, Storefront } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import TaxExportCard from "@/components/TaxExportCard";

const fmtPct = (n) => (n == null ? "—" : `${n}%`);
const fmtN = (n) => (n == null ? "—" : Number(n).toLocaleString());

export default function OperatorOSPanel() {
  const [churn, setChurn] = useState(null);
  const [cohort, setCohort] = useState(null);

  useEffect(() => {
    let cancel = false;
    Promise.allSettled([
      api.get("/me/operator-os/churn"),
      api.get("/me/operator-os/cohort"),
    ]).then(([cR, koR]) => {
      if (cancel) return;
      if (cR.status === "fulfilled") setChurn(cR.value.data);
      if (koR.status === "fulfilled") setCohort(koR.value.data);
    });
    return () => { cancel = true; };
  }, []);

  return (
    <section className="mt-12" data-testid="operator-os-panel">
      <div className="flex items-baseline gap-3 mb-3">
        <h2 className="font-serif-display text-2xl text-[#1C1B1A] flex items-center gap-2">
          <Storefront size={20} weight="duotone" className="text-[#4A5D4E]" /> Operator OS
        </h2>
        <span className="text-[10px] uppercase tracking-[0.22em] text-[#C16E5A]">v1</span>
      </div>
      <p className="text-xs text-[#6B6A68] mb-5 max-w-2xl">
        Your business at a glance. Cohort retention is the single best leading
        indicator of product-market fit; churn lets you reach out before the
        next one cancels; tax CSV makes April easy.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {/* Churn card */}
        <div className="bg-white border hairline rounded-xl p-5" data-testid="operator-os-churn-card">
          <div className="flex items-start justify-between mb-2">
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#6B6A68]">Churn · 30 days</div>
              <div className="font-serif-display text-3xl text-[#1C1B1A] mt-1 tabular-nums" data-testid="operator-os-churn-total">
                {fmtN(churn?.total_30d)}
              </div>
            </div>
            <div className="w-9 h-9 rounded-md bg-[#FDF1ED] flex items-center justify-center text-[#C16E5A] shrink-0">
              <UserMinus size={16} weight="duotone" />
            </div>
          </div>
          {churn?.recent?.length ? (
            <div className="mt-3 pt-3 border-t hairline">
              <div className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A68] mb-1.5">Most recent</div>
              <ul className="space-y-1">
                {churn.recent.slice(0, 3).map((r, i) => (
                  <li key={i} className="text-[11px] text-[#3A4A3E] truncate">
                    <strong>{r.silo_name}</strong> — {r.fan_email || "anonymous"}
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <p className="text-[11px] text-[#6B6A68] mt-2 italic">No cancellations in the last 30 days.</p>
          )}
        </div>

        {/* Cohort retention card */}
        <div className="bg-white border hairline rounded-xl p-5" data-testid="operator-os-cohort-card">
          <div className="flex items-start justify-between mb-2">
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#6B6A68]">Retention · 30 days</div>
              <div className="font-serif-display text-3xl text-[#1C1B1A] mt-1 tabular-nums" data-testid="operator-os-cohort-pct">
                {fmtPct(cohort?.retention_pct)}
              </div>
            </div>
            <div className="w-9 h-9 rounded-md bg-[#F0F1EE] flex items-center justify-center text-[#4A5D4E] shrink-0">
              <ChartLine size={16} weight="duotone" />
            </div>
          </div>
          <p className="text-[11px] text-[#6B6A68] mt-2">
            {cohort?.cohort_size
              ? <>Of {fmtN(cohort.cohort_size)} subscribers from 30 days ago, {fmtN(cohort.still_active)} are still paying.</>
              : "Once you have subscribers older than 30 days, this number unlocks."}
          </p>
        </div>

        {/* Tax CSV card — Oct 3 2026 (F7): its own component now that it picks
            a year and reports what is inside the file before you download it. */}
        <TaxExportCard />
      </div>
    </section>
  );
}
